import logger from '../../lib/logger';
import React, { useState, useRef } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import { 
  Download, 
  FileImage, 
  FileText,
  Loader2
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';

const ExportModal = ({ open, onClose, type = 'image' }) => {
  const { currentProject, sceneObjects, projectors } = useApp();
  const [exportFormat, setExportFormat] = useState(type === 'pdf' ? 'pdf' : 'png');
  const [includeSpecs, setIncludeSpecs] = useState(true);
  const [includePositions, setIncludePositions] = useState(true);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    
    try {
      if (exportFormat === 'pdf') {
        await exportPDF();
      } else {
        await exportImage();
      }
      toast.success('Export complete');
      onClose();
    } catch (error) {
      toast.error('Export failed');
      logger.error(error);
    } finally {
      setExporting(false);
    }
  };

  const exportImage = async () => {
    const canvas = document.getElementById('renderCanvas');
    if (!canvas) throw new Error('Canvas not found');

    const dataUrl = canvas.toDataURL(`image/${exportFormat}`);
    const link = document.createElement('a');
    link.download = `${currentProject?.name || 'iav-export'}-${Date.now()}.${exportFormat}`;
    link.href = dataUrl;
    link.click();
  };

  const exportPDF = async () => {
    const pdf = new jsPDF('landscape', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    
    // Title
    pdf.setFontSize(24);
    pdf.setTextColor(217, 70, 239); // Fuchsia
    pdf.text('IAV Project Report', 20, 20);
    
    // Project name
    pdf.setFontSize(16);
    pdf.setTextColor(60, 60, 60);
    pdf.text(currentProject?.name || 'Untitled Project', 20, 32);
    
    // Date
    pdf.setFontSize(10);
    pdf.setTextColor(120, 120, 120);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, 20, 40);
    
    // Screenshot
    const canvas = document.getElementById('renderCanvas');
    if (canvas) {
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 160;
      const imgHeight = (canvas.height / canvas.width) * imgWidth;
      pdf.addImage(imgData, 'PNG', 20, 50, imgWidth, Math.min(imgHeight, 100));
    }

    // Projector specs
    if (includeSpecs && projectors.length > 0) {
      let yPos = 160;
      
      pdf.setFontSize(14);
      pdf.setTextColor(60, 60, 60);
      pdf.text('Projector Specifications', 20, yPos);
      yPos += 10;

      pdf.setFontSize(9);
      projectors.forEach((proj, index) => {
        if (yPos > pageHeight - 20) {
          pdf.addPage();
          yPos = 20;
        }

        pdf.setTextColor(217, 70, 239);
        pdf.text(`${index + 1}. ${proj.name}`, 20, yPos);
        yPos += 6;

        pdf.setTextColor(80, 80, 80);
        if (proj.spec) {
          pdf.text(`   Resolution: ${proj.spec.resolution}`, 20, yPos);
          yPos += 5;
          pdf.text(`   Brightness: ${proj.spec.brightness.toLocaleString()} lumens`, 20, yPos);
          yPos += 5;
          pdf.text(`   Throw Ratio: ${proj.throw_ratio.toFixed(2)}`, 20, yPos);
          yPos += 5;
        }
        
        if (includePositions) {
          pdf.text(`   Position: X:${proj.position.x.toFixed(2)} Y:${proj.position.y.toFixed(2)} Z:${proj.position.z.toFixed(2)}`, 20, yPos);
          yPos += 5;
          pdf.text(`   Rotation: X:${proj.rotation.x.toFixed(2)} Y:${proj.rotation.y.toFixed(2)} Z:${proj.rotation.z.toFixed(2)}`, 20, yPos);
          yPos += 8;
        }
      });
    }

    // Scene objects
    if (includePositions && sceneObjects.length > 0) {
      pdf.addPage();
      let yPos = 20;
      
      pdf.setFontSize(14);
      pdf.setTextColor(60, 60, 60);
      pdf.text('Scene Objects', 20, yPos);
      yPos += 10;

      pdf.setFontSize(9);
      sceneObjects.forEach((obj, index) => {
        if (yPos > pageHeight - 20) {
          pdf.addPage();
          yPos = 20;
        }

        pdf.setTextColor(100, 100, 100);
        pdf.text(`${index + 1}. ${obj.name} (${obj.type})`, 20, yPos);
        yPos += 5;
        pdf.text(`   Position: X:${obj.position.x.toFixed(2)} Y:${obj.position.y.toFixed(2)} Z:${obj.position.z.toFixed(2)}`, 20, yPos);
        yPos += 5;
        pdf.text(`   Scale: X:${obj.scale.x.toFixed(2)} Y:${obj.scale.y.toFixed(2)} Z:${obj.scale.z.toFixed(2)}`, 20, yPos);
        yPos += 8;
      });
    }

    // Save
    pdf.save(`${currentProject?.name || 'iav-export'}-${Date.now()}.pdf`);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700" data-testid="export-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-fuchsia-400" />
            Export Project
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Format Selection */}
          <div className="space-y-3">
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">Export Format</Label>
            <RadioGroup value={exportFormat} onValueChange={setExportFormat}>
              <div className="flex items-center space-x-3 p-3 rounded-lg border border-zinc-700 hover:border-zinc-600 cursor-pointer">
                <RadioGroupItem value="png" id="png" />
                <label htmlFor="png" className="flex items-center gap-3 cursor-pointer flex-1">
                  <FileImage className="w-5 h-5 text-zinc-400" />
                  <div>
                    <div className="text-sm font-medium">PNG Image</div>
                    <div className="text-xs text-zinc-500">High quality screenshot</div>
                  </div>
                </label>
              </div>
              <div className="flex items-center space-x-3 p-3 rounded-lg border border-zinc-700 hover:border-zinc-600 cursor-pointer">
                <RadioGroupItem value="jpg" id="jpg" />
                <label htmlFor="jpg" className="flex items-center gap-3 cursor-pointer flex-1">
                  <FileImage className="w-5 h-5 text-zinc-400" />
                  <div>
                    <div className="text-sm font-medium">JPEG Image</div>
                    <div className="text-xs text-zinc-500">Compressed screenshot</div>
                  </div>
                </label>
              </div>
              <div className="flex items-center space-x-3 p-3 rounded-lg border border-zinc-700 hover:border-zinc-600 cursor-pointer">
                <RadioGroupItem value="pdf" id="pdf" />
                <label htmlFor="pdf" className="flex items-center gap-3 cursor-pointer flex-1">
                  <FileText className="w-5 h-5 text-zinc-400" />
                  <div>
                    <div className="text-sm font-medium">PDF Document</div>
                    <div className="text-xs text-zinc-500">Technical documentation with specs</div>
                  </div>
                </label>
              </div>
            </RadioGroup>
          </div>

          {/* PDF Options */}
          {exportFormat === 'pdf' && (
            <div className="space-y-3">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Include in PDF</Label>
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  <Checkbox 
                    id="includeSpecs" 
                    checked={includeSpecs}
                    onCheckedChange={setIncludeSpecs}
                  />
                  <label htmlFor="includeSpecs" className="text-sm cursor-pointer">
                    Projector specifications
                  </label>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox 
                    id="includePositions" 
                    checked={includePositions}
                    onCheckedChange={setIncludePositions}
                  />
                  <label htmlFor="includePositions" className="text-sm cursor-pointer">
                    Object positions and transforms
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={onClose}
            className="border-zinc-700"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleExport}
            className="bg-fuchsia-600 hover:bg-fuchsia-500"
            disabled={exporting}
            data-testid="export-btn"
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportModal;
